import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { VideoLibraryItem } from "../../../../../types/Community";
import { getVideoLibrary } from "../../../../../database/community/videoLibrary";
import { Muted } from "../shared";

// Picks one media item from the community library to become a post's image, so
// a per-platform post can carry a different image than its siblings.

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: var(--space-6) var(--space-3);
  overflow-y: auto;
  z-index: 50;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 640px;
  background: var(--color-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg, var(--shadow-md));
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
`;

const Title = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const Spacer = styled.span`
  flex: 1 1 auto;
`;

const CloseButton = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: var(--color-text-tertiary);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: var(--color-text);
  }
`;

const SearchInput = styled.input`
  appearance: none;
  width: 100%;
  padding: 8px 12px;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: var(--space-2);
  max-height: 60vh;
  overflow-y: auto;
`;

const Cell = styled.button`
  appearance: none;
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface-subtle);
  border-radius: var(--radius-md);
  padding: 0;
  cursor: pointer;
  overflow: hidden;
  aspect-ratio: 4 / 5;
  position: relative;

  &:hover {
    border-color: var(--color-accent);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const VideoBadge = styled.span`
  position: absolute;
  left: 4px;
  bottom: 4px;
  padding: 1px 5px;
  border-radius: var(--radius-pill);
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-family: var(--font-sans);
  font-size: 9px;
  font-weight: var(--font-weight-semibold);
`;

const StateNote = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  padding: var(--space-6) 0;
  text-align: center;
`;

export type MediaPickerModalProps = {
  communityId?: string;
  onPick: (item: VideoLibraryItem) => void;
  onClose: () => void;
};

export const MediaPickerModal: React.FC<MediaPickerModalProps> = ({
  communityId,
  onPick,
  onClose,
}) => {
  const [items, setItems] = useState<VideoLibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!communityId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    getVideoLibrary(communityId)
      .then((list) => {
        if (!cancelled) setItems(list);
      })
      .catch((err) => {
        console.error("Error loading media library for picker:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [communityId]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (it) =>
        it.name?.toLowerCase().includes(needle) ||
        (it.tags || []).some((t) => t.toLowerCase().includes(needle)) ||
        it.folder?.toLowerCase().includes(needle)
    );
  }, [items, q]);

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Head>
          <Title>Choose an image</Title>
          <Spacer />
          <CloseButton type="button" aria-label="Close" onClick={onClose}>
            ×
          </CloseButton>
        </Head>
        <SearchInput
          placeholder="Search by name, tag, or folder"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {loading ? (
          <StateNote>Loading library…</StateNote>
        ) : filtered.length === 0 ? (
          <StateNote>
            {items.length === 0
              ? "No media in the library yet. Upload some in the Library section."
              : "Nothing matches that search."}
          </StateNote>
        ) : (
          <Grid>
            {filtered.map((item) => (
              <Cell
                key={item.id}
                type="button"
                title={item.name}
                onClick={() => onPick(item)}
              >
                <img
                  src={item.mediaThumbnail || item.mediaUrl}
                  alt={item.name}
                  loading="lazy"
                />
                {item.type === "video" && <VideoBadge>VIDEO</VideoBadge>}
              </Cell>
            ))}
          </Grid>
        )}
        <Muted>Picking an image replaces this post's media only.</Muted>
      </Modal>
    </Overlay>
  );
};

export default MediaPickerModal;
